import { BehaviorSubject } from 'rxjs';
import * as yup from 'yup';

const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ''];

export default class EmailChangeBloc {
  loading$ = new BehaviorSubject(false);
  errMessage$ = new BehaviorSubject('');

  email$ = new BehaviorSubject('');
  isTokenValid$ = new BehaviorSubject(false);

  constructor({ userRepository, authRepository, emailChangeRepository }) {
    this.userRepository = userRepository;
    this.authRepository = authRepository;

    this.mfaNumber$ = emailChangeRepository.mfaNumber$;
    this.stepIndex$ = emailChangeRepository.stepIndex$;
  }

  initState() {
    this.loading$.next(false);
    this.errMessage$.next('');
    this.email$.next('');
    this.isTokenValid$.next(false);
  }

  initStepState() {
    this.mfaNumber$.next('');
    this.isTokenValid$.next(false);
    this.stepIndex$.next(0);
  }

  async checkCurrentState() {
    this.initState();

    const stepIndex = this.stepIndex$.getValue();
    const mfaNumber = this.mfaNumber$.getValue();

    if (stepIndex === 2 && mfaNumber?.length === 6) {
      this.checkCurrentMfaNumber();
      return;
    }

    this.initStepState();
  }

  async checkCurrentMfaNumber() {
    const mfaNumber = this.mfaNumber$.getValue();
    this.loading$.next(true);

    try {
      await this.validateMfaChangeEmail({ token: mfaNumber });
      this.isTokenValid$.next(true);
    } catch (err) {
      this.initStepState();
    } finally {
      this.loading$.next(false);
    }
  }

  nextStep() {
    this.stepIndex$.next(this.stepIndex$.getValue() + 1);
  }

  setMfaNumber(value) {
    const lastValue = value[value.length - 1] || '';

    if (!allowedKeys.includes(lastValue)) {
      return;
    }

    this.errMessage$.next('');

    if (value?.length > 6) {
      return;
    }

    this.mfaNumber$.next(value);
  }

  setEmail(value) {
    this.email$.next(value);

    const emailSchema = yup.string().email('Invalid email format');

    try {
      emailSchema.validateSync(value);
      this.errMessage$.next('');
    } catch (error) {
      this.errMessage$.next(error.message || '');
    }
  }

  async proceedClick() {
    const stepIndex = this.stepIndex$.getValue();

    try {
      this.loading$.next(true);

      if (stepIndex === 0) await this.requestEmailChange();
      if (stepIndex === 1) {
        await this.validateMfaChangeEmail({ token: this.mfaNumber$.getValue() });
        this.isTokenValid$.next(true);
      }
      if (stepIndex === 2) {
        await this.changeEmail({ email: this.email$.getValue(), token: this.mfaNumber$.getValue() });
      }

      this.nextStep();
    } catch (err) {
      let defaultError = 'Something went wrong on requesting email change. Please contact administrator.';
      if (stepIndex === 1) defaultError = 'Something went wrong on validating token. Please contact administrator.';
      if (stepIndex === 2) defaultError = 'Something went wrong on changing email. Please contact administrator.';

      console.log(err);
      this.errMessage$.next(err.message || defaultError);
    } finally {
      this.loading$.next(false);
    }
  }

  changeRequestSuccess() {
    this.authRepository.logout();
  }

  async requestEmailChange() {
    await this.userRepository.changeEmailRequest();
  }

  async validateMfaChangeEmail({ token }) {
    await this.userRepository.validateMfaChangeEmail({ token });
  }

  async changeEmail({ token, email }) {
    await this.userRepository.changeEmail({ email, token });
  }
}
